(() => {
  class StickyATCSync extends HTMLElement {
    constructor() {
      super();
      this.isSyncing = false;
      this.unsubscribeVariant = undefined;
      // Stable bound handlers
      this._onMainVariantIdChange = this.onMainVariantIdChangeImpl.bind(this);
      this._onMainQtyChange = this.onMainQtyChangeImpl.bind(this);
      this._onStickyQtyChange = this.onStickyQtyChangeImpl.bind(this);
      this._isForwardingQtyClick = false;
      this._stickyMinusListener = undefined;
      this._stickyPlusListener = undefined;
      this._mainMinusMirror = undefined;
      this._mainPlusMirror = undefined;
      this._mainVariantIdInputRef = undefined;
      this._mainQtyInputRef = undefined;
      this._mainInlineQtyInputRef = undefined;
    }

    connectedCallback() {
      this.stickyRoot = this.closest('.sticky-atc') || document.querySelector('.sticky-atc');
      if (!this.stickyRoot) return;

      // Parse data from JSON script tags rendered by the section
      const sectionId = this.stickyRoot.id.replace('StickyATC-', '');
      const variantsEl = document.getElementById(`StickyATCVariants-${sectionId}`);
      const optionsEl = document.getElementById(`StickyATCOptions-${sectionId}`);
      this.variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];
      this.optionsMeta = optionsEl ? JSON.parse(optionsEl.textContent) : [];

      // Main product-info and elements
      this.productInfo = document.querySelector('product-info');
      if (!this.productInfo) return;
      this.mainSectionId = this.productInfo.dataset.section || this.productInfo.dataset.originalSection;
      this.buildMainControlMapsFromCurrent();

      // Sticky elements
      this.stickyVariantIdInput = this.stickyRoot.querySelector('.product-variant-id');
      this.stickyVariantSelects = Array.from(
        this.stickyRoot.querySelectorAll(`#variant-selects-${sectionId} select`)
      );
      this.stickySelectByIndex = new Map();
      this.stickyVariantSelects.forEach((sel) => {
        const idx = this.parseIndexFromId(sel.id);
        if (idx !== null) this.stickySelectByIndex.set(idx, sel);
      });
      this.stickyQtyInput = this.stickyRoot.querySelector(`#Quantity-${sectionId}-inline`);

      this.colorIndex = this.optionsMeta.findIndex((o) => /color|colour/i.test(o.name));
      this.sizeIndex = this.optionsMeta.findIndex((o) => /size/i.test(o.name));
      this.colorNameEl = this.stickyRoot.querySelector('.sticky-atc__color-name');
      this.colorSwatchEl = this.stickyRoot.querySelector('.sticky-atc__color-swatch');
      this.sizeSelect = this.sizeIndex > -1 ? this.stickyRoot.querySelector(`#Option-${sectionId}-${this.sizeIndex}`) : null;
      this.sizeDropdown = this.sizeSelect ? this.sizeSelect.closest('custom-dropdown') : null;

      this.bindEvents();
      // Initialize sticky UI from current main variant
      this.reflectMainVariantToSticky();
      this.syncQuantityBothWays(true);
      // Normalize sticky selects so VariantSelects.updateSelectionMetadata can safely toggle attributes
      this.normalizeStickySelectedAttributes();
      // Mirror +/- buttons between sticky and main
      this.bindQtyButtonMirroring();
    }

    disconnectedCallback() {
      if (typeof this.unsubscribeVariant === 'function') this.unsubscribeVariant();
      this.removeEventListeners();
    }

    bindEvents() {
      // From sticky → main: when user changes sticky selects
      this.onStickyChange = this.onStickyChange?.bind(this) || this.onStickyChangeImpl.bind(this);
      this.stickyVariantSelects.forEach((sel) => sel.addEventListener('change', this.onStickyChange));

      // From main → sticky: subscribe to variantChange if pubsub exists, else fallback to hidden input change
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (!data?.variant) return;
          this.updateStickyFromVariant(data.variant);
          this.dispatchEvent(new CustomEvent('product:variant-synced', { detail: { variantId: data.variant.id, source: 'main' } }));
        });
      }

      this.attachMainControlListeners();

      // Quantity sync both ways
      this.stickyQtyInput?.addEventListener('change', this._onStickyQtyChange);
      this.stickyQtyInput?.addEventListener('input', this._onStickyQtyChange);
    }

    removeEventListeners() {
      this.stickyVariantSelects.forEach((sel) => sel.removeEventListener('change', this.onStickyChange));
      this.mainVariantIdInput?.removeEventListener('change', this.onMainVariantIdChange);
      this.stickyQtyInput?.removeEventListener('change', this.onStickyQtyChange);
      this.mainQtyInput?.removeEventListener('change', this.onMainQtyChange);
    }

    onStickyChangeImpl(event) {
      if (this.isSyncing) return;
      const changedSelect = event?.target;
      const changedIdx = changedSelect ? this.parseIndexFromId(changedSelect.id) : null;
      // Build full ordered options array by index
      const fullOptions = this.optionsMeta.map((_, idx) => {
        if (idx === this.colorIndex) {
          const cur = this.currentMainVariant();
          return cur ? cur.options[this.colorIndex] : this.mainSelectByIndex.get(idx)?.value;
        }
        const stickySel = this.stickySelectByIndex.get(idx);
        if (stickySel) return stickySel.value;
        const mainSel = this.mainSelectByIndex.get(idx);
        return mainSel ? mainSel.value : undefined;
      });
      this.isSyncing = true;
      try {
        const variant = this.findVariantByOptions(fullOptions);
        if (variant) {
          // Update sticky form id immediately
          this.setStickyVariantId(variant.id);
          // Drive main product via Section Rendering API
          this.updateMainProductBySectionAPI(variant.id);
          // Broadcast
          window.dispatchEvent(
            new CustomEvent('product:switch-variant', { detail: { variantId: variant.id, source: 'sticky' } })
          );
        }
      } finally {
        // Let main flow publish variantChange and then we will reflect back via subscriber
        setTimeout(() => (this.isSyncing = false), 0);
      }
    }

    onStickyQtyChangeImpl() {
      if (!this.mainQtyInput || !this.stickyQtyInput) return;
      if (this.isSyncing) return;
      this.isSyncing = true;
      try {
        this.mainQtyInput.value = this.stickyQtyInput.value;
        this.mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        if (this.mainInlineQtyInput) {
          this.mainInlineQtyInput.value = this.stickyQtyInput.value;
          this.mainInlineQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.dispatchEvent(
          new CustomEvent('product:quantity-synced', { detail: { quantity: this.stickyQtyInput.value, source: 'sticky' } })
        );
      } finally {
        setTimeout(() => (this.isSyncing = false), 0);
      }
    }

    onMainQtyChangeImpl() {
      if (!this.mainQtyInput || !this.stickyQtyInput) return;
      // Do not early-return on isSyncing here; allow main to drive sticky always
      const value = this.mainQtyInput.value;
      if (this.stickyQtyInput.value !== value) {
        this.stickyQtyInput.value = value;
        this.stickyQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (this.mainInlineQtyInput && this.mainInlineQtyInput.value !== value) {
        this.mainInlineQtyInput.value = value;
        this.mainInlineQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.dispatchEvent(
        new CustomEvent('product:quantity-synced', { detail: { quantity: value, source: 'main' } })
      );
    }

    syncQuantityBothWays(initial = false) {
      if (!this.mainQtyInput || !this.stickyQtyInput) return;
      const source = initial ? this.mainQtyInput : this.stickyQtyInput;
      const target = initial ? this.stickyQtyInput : this.mainQtyInput;
      target.value = source.value;
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }

    reflectMainVariantToSticky() {
      const id = this.mainVariantIdInput?.value;
      if (!id) return;
      const v = this.variants.find((x) => String(x.id) === String(id));
      if (v) this.updateStickyFromVariant(v);
    }

    onMainVariantIdChangeImpl() {
      const id = this.mainVariantIdInput?.value;
      if (!id) return;
      const v = this.variants.find((x) => String(x.id) === String(id));
      if (v) this.updateStickyFromVariant(v);
    }

    updateStickyFromVariant(variant) {
      if (!variant) return;
      // Update selects UI
      this.stickySelectByIndex.forEach((sel, idx) => {
        if (!sel) return;
        const value = variant.options[idx];
        // Set select value
        sel.value = value;
        // Ensure selected attribute reflects the chosen value
        try {
          Array.from(sel.options)
            .filter((o) => o.getAttribute('selected'))
            .forEach((o) => o.removeAttribute('selected'));
          const opt = Array.from(sel.options).find((o) => o.value === value);
          if (opt) opt.setAttribute('selected', 'selected');
        } catch (e) {}
        // Update dropdown swatch preview if present
        const selectedOption = sel.selectedOptions && sel.selectedOptions[0];
        const swatchValue = selectedOption ? selectedOption.dataset.optionSwatchValue : null;
        const selectedDropdownSwatchValue = sel
          .closest('.product-form__input')
          ?.querySelector('[data-selected-value] > .swatch');
        if (selectedDropdownSwatchValue) {
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }
          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            selectedOption?.dataset.optionSwatchFocalPoint || 'unset'
          );
        }
      });
      // Update color display
      this.setColorFromVariant(variant);
      // Rebuild size options to reflect availability for the chosen color
      if (this.colorIndex > -1 && this.sizeIndex > -1) {
        this.rebuildSizeOptionsForColor(variant.options[this.colorIndex], variant);
      }
      // Update sticky form variant id
      this.setStickyVariantId(variant.id);
    }

    setStickyVariantId(id) {
      if (!this.stickyVariantIdInput) return;
      this.stickyVariantIdInput.value = id;
      this.stickyVariantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    setColorFromVariant(variant) {
      if (this.colorIndex < 0) return;
      const clr = variant.options[this.colorIndex];
      if (this.colorNameEl) this.colorNameEl.textContent = clr;
      try {
        const opt = this.optionsMeta[this.colorIndex];
        const val = (opt?.values || []).find((v) => (v.value || v) === clr);
        if (val && val.swatch) {
          if (val.swatch.image && this.colorSwatchEl) {
            const src = val.swatch.image.src || val.swatch.image;
            this.colorSwatchEl.style.backgroundImage = `url(${src})`;
            this.colorSwatchEl.style.backgroundColor = '';
          } else if (val.swatch.color && this.colorSwatchEl) {
            this.colorSwatchEl.style.backgroundImage = '';
            this.colorSwatchEl.style.backgroundColor = `rgb(${val.swatch.color.rgb})`;
          }
        }
      } catch (e) {}
    }

    rebuildSizeOptionsForColor(colorValue, currentVariant) {
      if (!this.sizeSelect || this.colorIndex < 0 || this.sizeIndex < 0) return;
      const optionsForColor = this.variants.filter((v) => v.options[this.colorIndex] === colorValue);
      const uniqueSizes = [];
      const seen = new Set();
      optionsForColor.forEach((v) => {
        const s = v.options[this.sizeIndex];
        if (!seen.has(s)) {
          seen.add(s);
          uniqueSizes.push({ label: s, variant: v });
        }
      });
      const curSize = currentVariant ? currentVariant.options[this.sizeIndex] : this.sizeSelect.value;
      this.sizeSelect.innerHTML = uniqueSizes
        .map(({ label, variant }) => `<option value="${label}" ${label === curSize ? 'selected' : ''} ${variant.available ? '' : 'disabled'}>${label}</option>`) // eslint-disable-line
        .join('');
      if (this.sizeDropdown && typeof this.sizeDropdown.updateOptionStates === 'function') {
        this.sizeDropdown.updateOptionStates();
        const btnValue = this.sizeDropdown.querySelector('.custom-dropdown__value');
        if (btnValue) btnValue.textContent = curSize || uniqueSizes[0]?.label || '';
      }
      // Ensure selected attribute present on selected option
      this.ensureSelectHasSelectedAttribute(this.sizeSelect);
    }

    findVariantByOptions(selectedOptions) {
      return this.variants.find((v) => JSON.stringify(v.options) === JSON.stringify(selectedOptions));
    }

    parseIndexFromId(id) {
      // Expect pattern: Option-<sectionId>-<index>
      if (!id) return null;
      const parts = id.split('-');
      const last = parts[parts.length - 1];
      const idx = parseInt(last, 10);
      return Number.isFinite(idx) ? idx : null;
    }

    currentMainVariant() {
      const id = this.mainVariantIdInput?.value;
      if (!id) return null;
      return this.variants.find((x) => String(x.id) === String(id)) || null;
    }

    setMainOptionByIndex(idx, value, trigger = false) {
      // Try select first
      const select = this.mainSelectByIndex.get(idx);
      if (select) {
        if (select.value !== value) select.value = value;
        // Keep DOM option[selected] in sync to satisfy VariantSelects.updateSelectionMetadata
        this.ensureSelectHasSelectedAttribute(select);
        if (trigger) select.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      // Fallback to radios by name
      const radios = this.mainRadiosByIndex.get(idx);
      if (radios && radios.length) {
        // Find matching radio by value
        const target = radios.find((r) => r.value === value);
        if (target && !target.checked) {
          target.checked = true;
        }
        if (trigger && target) target.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    normalizeStickySelectedAttributes() {
      this.stickySelectByIndex.forEach((sel) => this.ensureSelectHasSelectedAttribute(sel));
    }

    ensureSelectHasSelectedAttribute(sel) {
      if (!sel) return;
      try {
        const selected = sel.selectedOptions && sel.selectedOptions[0];
        if (!selected) return;
        // Remove any stale selected attributes
        Array.from(sel.options)
          .filter((o) => o.getAttribute('selected'))
          .forEach((o) => o.removeAttribute('selected'));
        selected.setAttribute('selected', 'selected');
      } catch (e) {}
    }

    collectSelectedOptionValueIdsFromMain() {
      return this.optionsMeta
        .map((opt, idx) => {
          const sel = this.mainSelectByIndex.get(idx);
          if (sel && sel.selectedOptions && sel.selectedOptions[0]) {
            return sel.selectedOptions[0].dataset.optionValueId;
          }
          const radios = this.mainRadiosByIndex.get(idx);
          if (radios && radios.length) {
            const checked = radios.find((r) => r.checked);
            return checked ? checked.dataset.optionValueId : undefined;
          }
          return undefined;
        })
        .filter(Boolean);
    }

    bindQtyButtonMirroring() {
      const stickyWrapper = this.stickyQtyInput?.closest('quantity-input');
      const mainWrapper = this.mainQtyInput?.closest('quantity-input');
      if (!stickyWrapper || !mainWrapper) return;

      const stickyMinus = stickyWrapper.querySelector(".quantity__button[name='minus']");
      const stickyPlus = stickyWrapper.querySelector(".quantity__button[name='plus']");
      const mainMinus = mainWrapper.querySelector(".quantity__button[name='minus']");
      const mainPlus = mainWrapper.querySelector(".quantity__button[name='plus']");
      if (!stickyMinus || !stickyPlus || !mainMinus || !mainPlus) return;

      // Remove old listeners if re-binding
      if (this._stickyMinusListener) stickyMinus.removeEventListener('click', this._stickyMinusListener);
      if (this._stickyPlusListener) stickyPlus.removeEventListener('click', this._stickyPlusListener);
      if (this._mainMinusMirror) mainMinus.removeEventListener('click', this._mainMinusMirror);
      if (this._mainPlusMirror) mainPlus.removeEventListener('click', this._mainPlusMirror);

      const applyStep = (direction) => {
        if (!this.mainQtyInput) return;
        const input = this.mainQtyInput;
        const previousValue = input.value;
        const minAttr = input.getAttribute('min');
        const stepAttr = input.getAttribute('step');
        const min = minAttr ? parseInt(minAttr) : 1;
        const step = stepAttr ? parseInt(stepAttr) : 1;

        if (direction === 'plus') {
          if (min > step && input.value == 0) {
            input.value = String(min);
          } else {
            try { input.stepUp(); } catch (e) { input.value = String((parseInt(input.value || '0') || 0) + step); }
          }
        } else {
          try { input.stepDown(); } catch (e) { input.value = String(Math.max(min, (parseInt(input.value || '0') || 0) - step)); }
          if (String(min) === previousValue) input.value = String(min);
        }

        if (previousValue !== input.value) {
          input.dispatchEvent(new Event('change', { bubbles: true }));
          if (this.mainInlineQtyInput) {
            this.mainInlineQtyInput.value = input.value;
            this.mainInlineQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        // Reflect into sticky UI
        if (this.stickyQtyInput) {
          this.stickyQtyInput.value = input.value;
          this.stickyQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      };

      // IMPORTANT: Do NOT forward sticky +/- clicks to main to avoid double step.
      // Rely on sticky quantity-input to update its value and trigger our onStickyQtyChangeImpl.
      this._stickyMinusListener = undefined;
      this._stickyPlusListener = undefined;

      // Mirror main button clicks back to sticky quickly
      this._mainMinusMirror = () => { setTimeout(() => this.onMainQtyChangeImpl(), 0); };
      this._mainPlusMirror = () => { setTimeout(() => this.onMainQtyChangeImpl(), 0); };
      mainMinus.addEventListener('click', this._mainMinusMirror);
      mainPlus.addEventListener('click', this._mainPlusMirror);
    }

    updateMainProductBySectionAPI(variantId) {
      try {
        const sectionId = this.mainSectionId;
        const baseUrl = this.productInfo?.dataset?.url;
        if (!sectionId || !baseUrl) return;
        const url = `${baseUrl}?variant=${encodeURIComponent(variantId)}&section_id=${encodeURIComponent(sectionId)}`;
        fetch(url)
          .then((r) => r.text())
          .then((htmlText) => {
            const html = new DOMParser().parseFromString(htmlText, 'text/html');
            const newNode = html.querySelector('product-info');
            const currentNode = this.productInfo;
            if (!newNode || !currentNode) return;
            if (typeof HTMLUpdateUtility !== 'undefined') {
              HTMLUpdateUtility.viewTransition(currentNode, newNode, [], []);
            } else {
              const wrapper = document.createElement('div');
              wrapper.innerHTML = newNode.outerHTML;
              currentNode.replaceWith(wrapper.firstElementChild);
            }
            // Re-bind main controls after swap
            this.productInfo = document.querySelector('product-info');
            this.buildMainControlMapsFromCurrent();
            this.attachMainControlListeners();
            this.bindQtyButtonMirroring();
            // Update URL after DOM is ready
            try { window.history.replaceState({}, '', `${baseUrl}?variant=${variantId}`); } catch (e) {}
            // Publish variantChange to update any subscribers
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              const matched = this.variants.find((v) => String(v.id) === String(variantId));
              publish(PUB_SUB_EVENTS.variantChange, {
                data: {
                  sectionId,
                  html,
                  variant: matched || { id: variantId },
                },
              });
            }
          })
          .catch(() => {});
      } catch (e) {}
    }

    buildMainControlMapsFromCurrent() {
      this.mainVariantIdInput = document.querySelector('.product__info-container .product-variant-id');
      this.mainVariantSelects = Array.from(this.productInfo.querySelectorAll('variant-selects select'));
      this.mainSelectByIndex = new Map();
      this.mainRadiosByIndex = new Map();
      this.mainVariantSelects.forEach((sel) => {
        const idx = this.parseIndexFromId(sel.id);
        if (idx !== null) this.mainSelectByIndex.set(idx, sel);
      });
      this.optionsMeta.forEach((opt, idx) => {
        const radioName = `${opt.name}-${opt.position}`;
        const radios = Array.from(
          this.productInfo.querySelectorAll(
            `variant-selects input[type="radio"][name="${CSS.escape(radioName)}"]`
          )
        );
        if (radios.length) this.mainRadiosByIndex.set(idx, radios);
      });
      this.mainQtyInput = document.getElementById(`Quantity-${this.mainSectionId}`);
      this.mainInlineQtyInput = document.getElementById(`Quantity-${this.mainSectionId}-inline`);
    }

    attachMainControlListeners() {
      // Re-attach freshly every time (prevent duplicates by removing old ones first)
      if (this._mainVariantIdInputRef) this._mainVariantIdInputRef.removeEventListener('change', this._onMainVariantIdChange);
      if (this._mainQtyInputRef) {
        this._mainQtyInputRef.removeEventListener('change', this._onMainQtyChange);
        this._mainQtyInputRef.removeEventListener('input', this._onMainQtyChange);
      }
      if (this._mainInlineQtyInputRef) {
        this._mainInlineQtyInputRef.removeEventListener('change', this._onMainQtyChange);
        this._mainInlineQtyInputRef.removeEventListener('input', this._onMainQtyChange);
      }

      if (this.mainVariantIdInput) {
        this.mainVariantIdInput.addEventListener('change', this._onMainVariantIdChange);
        this._mainVariantIdInputRef = this.mainVariantIdInput;
      }
      if (this.mainQtyInput) {
        this.mainQtyInput.addEventListener('change', this._onMainQtyChange);
        this.mainQtyInput.addEventListener('input', this._onMainQtyChange);
        this._mainQtyInputRef = this.mainQtyInput;
      }
      if (this.mainInlineQtyInput) {
        this.mainInlineQtyInput.addEventListener('change', this._onMainQtyChange);
        this.mainInlineQtyInput.addEventListener('input', this._onMainQtyChange);
        this._mainInlineQtyInputRef = this.mainInlineQtyInput;
      }
    }
  }

  if (!customElements.get('sticky-atc-sync')) {
    customElements.define('sticky-atc-sync', StickyATCSync);
  }
})();


